import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Match3.css';

const Match3 = () => {
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [moves, setMoves] = useState(30);
  const [gameOver, setGameOver] = useState(false);
  const [matchingCells, setMatchingCells] = useState(new Set());
  const [animationKey, setAnimationKey] = useState(0);

  // Define colors and their corresponding shapes - memoized to prevent recreation
  const pieceTypes = useMemo(() => [
    { color: '#e74c3c', shape: 'circle', name: 'red' },      // Red circle
    { color: '#f39c12', shape: 'square', name: 'orange' },   // Orange square
    { color: '#f1c40f', shape: 'diamond', name: 'yellow' },  // Yellow diamond
    { color: '#27ae60', shape: 'triangle', name: 'green' },  // Green triangle
    { color: '#3498db', shape: 'oval', name: 'blue' },       // Blue oval
    { color: '#9b59b6', shape: 'star', name: 'purple' }      // Purple star
  ], []);
  
  const boardSize = 8;

  const createRandomBoard = useCallback(() => {
    const newBoard = [];
    for (let row = 0; row < boardSize; row++) {
      const newRow = [];
      for (let col = 0; col < boardSize; col++) {
        let pieceType;
        do {
          pieceType = pieceTypes[Math.floor(Math.random() * pieceTypes.length)];
        } while (
          (col >= 2 && newRow[col - 1]?.name === pieceType.name && newRow[col - 2]?.name === pieceType.name) ||
          (row >= 2 && newBoard[row - 1] && newBoard[row - 1][col]?.name === pieceType.name && 
           newBoard[row - 2] && newBoard[row - 2][col]?.name === pieceType.name)
        );
        newRow.push(pieceType);
      }
      newBoard.push(newRow);
    }
    return newBoard;
  }, [pieceTypes]);

  const findMatches = useCallback((board) => {
    const matches = new Set();

    // Check horizontal matches
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize - 2; col++) {
        const piece = board[row][col];
        if (piece && board[row][col + 1]?.name === piece.name && board[row][col + 2]?.name === piece.name) {
          matches.add(`${row}-${col}`);
          matches.add(`${row}-${col + 1}`);
          matches.add(`${row}-${col + 2}`);
          
          // Check for longer matches
          let extraCol = col + 3;
          while (extraCol < boardSize && board[row][extraCol]?.name === piece.name) {
            matches.add(`${row}-${extraCol}`);
            extraCol++;
          }
        }
      }
    }

    // Check vertical matches
    for (let col = 0; col < boardSize; col++) {
      for (let row = 0; row < boardSize - 2; row++) {
        const piece = board[row][col];
        if (piece && board[row + 1][col]?.name === piece.name && board[row + 2][col]?.name === piece.name) {
          matches.add(`${row}-${col}`);
          matches.add(`${row + 1}-${col}`);
          matches.add(`${row + 2}-${col}`);
          
          // Check for longer matches
          let extraRow = row + 3;
          while (extraRow < boardSize && board[extraRow][col]?.name === piece.name) {
            matches.add(`${extraRow}-${col}`);
            extraRow++;
          }
        }
      }
    }

    return matches;
  }, []);

  const removeMatches = useCallback((board, matches) => {
    const newBoard = board.map(row => [...row]);
    matches.forEach(match => {
      const [row, col] = match.split('-').map(Number);
      newBoard[row][col] = null;
    });
    return newBoard;
  }, []);

  const dropPieces = useCallback((board) => {
    const newBoard = board.map(row => [...row]);
    
    // Process each column from bottom to top
    for (let col = 0; col < boardSize; col++) {
      // Collect all non-null pieces in this column
      const pieces = [];
      for (let row = 0; row < boardSize; row++) {
        if (newBoard[row][col]) {
          pieces.push(newBoard[row][col]);
        }
      }
      
      // Clear the column
      for (let row = 0; row < boardSize; row++) {
        newBoard[row][col] = null;
      }
      
      // Fill from bottom with existing pieces
      for (let i = 0; i < pieces.length; i++) {
        newBoard[boardSize - 1 - i][col] = pieces[pieces.length - 1 - i];
      }
      
      // Fill empty spaces at top with new pieces
      for (let row = 0; row < boardSize - pieces.length; row++) {
        newBoard[row][col] = pieceTypes[Math.floor(Math.random() * pieceTypes.length)];
      }
    }
    
    return newBoard;
  }, [pieceTypes]);

  const processMatches = useCallback(async (board) => {
    let currentBoard = board;
    let totalMatches = 0;
    let hasMatches = true;

    while (hasMatches) {
      const matches = findMatches(currentBoard);
      if (matches.size === 0) {
        hasMatches = false;
      } else {
        // Animate matching pieces
        setMatchingCells(matches);
        await new Promise(resolve => setTimeout(resolve, 400));
        
        totalMatches += matches.size;
        currentBoard = removeMatches(currentBoard, matches);
        setMatchingCells(new Set());
        
        // Update board to trigger CSS animations
        setBoard([...currentBoard]);
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Drop pieces with animation
        currentBoard = dropPieces(currentBoard);
        setBoard([...currentBoard]);
        
        // Wait for drop animation to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return { board: currentBoard, matchCount: totalMatches };
  }, [findMatches, removeMatches, dropPieces]);

  const isAdjacent = (cell1, cell2) => {
    const [row1, col1] = cell1;
    const [row2, col2] = cell2;
    return (
      (Math.abs(row1 - row2) === 1 && col1 === col2) ||
      (Math.abs(col1 - col2) === 1 && row1 === row2)
    );
  };

  const swapPieces = (board, cell1, cell2) => {
    const newBoard = board.map(row => [...row]);
    const [row1, col1] = cell1;
    const [row2, col2] = cell2;
    
    [newBoard[row1][col1], newBoard[row2][col2]] = [newBoard[row2][col2], newBoard[row1][col1]];
    return newBoard;
  };

  const handleCellClick = async (row, col) => {
    if (isAnimating || gameOver) return;

    if (selectedCell === null) {
      setSelectedCell([row, col]);
    } else {
      const [selectedRow, selectedCol] = selectedCell;
      
      if (selectedRow === row && selectedCol === col) {
        // Deselect if clicking the same cell
        setSelectedCell(null);
      } else if (isAdjacent(selectedCell, [row, col])) {
        // Valid swap
        setIsAnimating(true);
        
        const swappedBoard = swapPieces(board, selectedCell, [row, col]);
        setBoard(swappedBoard);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const { board: newBoard, matchCount } = await processMatches(swappedBoard);
        setBoard(newBoard);
        
        if (matchCount > 0) {
          // Valid move
          setScore(prev => prev + matchCount * 10);
          setMoves(prev => prev - 1);
        } else {
          // Invalid move - swap back
          setBoard(swapPieces(swappedBoard, selectedCell, [row, col]));
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        setSelectedCell(null);
        setIsAnimating(false);
      } else {
        // Select new cell
        setSelectedCell([row, col]);
      }
    }
  };

  const resetGame = useCallback(async () => {
    setIsAnimating(true);
    const newBoard = createRandomBoard();
    setBoard(newBoard);
    setMatchingCells(new Set());
    setAnimationKey(prev => prev + 1);
    
    // Process initial matches
    await new Promise(resolve => setTimeout(resolve, 100));
    const { board } = await processMatches(newBoard);
    setBoard(board);
    setScore(0);
    setMoves(30);
    setGameOver(false);
    setSelectedCell(null);
    setIsAnimating(false);
  }, [createRandomBoard, processMatches]);

  useEffect(() => {
    resetGame();
  }, [resetGame]);

  useEffect(() => {
    if (moves <= 0 && !gameOver) {
      setGameOver(true);
    }
  }, [moves, gameOver]);

  // Function to render different shapes
  const renderShape = (piece) => {
    if (!piece) return null;
    
    const { color, shape } = piece;
    
    switch (shape) {
      case 'circle':
        return <div className="shape circle" style={{ backgroundColor: color }} />;
      case 'square':
        return <div className="shape square2" style={{ backgroundColor: color }} />;
      case 'diamond':
        return <div className="shape diamond" style={{ backgroundColor: color }} />;
      case 'triangle':
        return <div className="shape triangle" style={{ borderBottomColor: color }} />;
      case 'oval':
        return <div className="shape oval" style={{ backgroundColor: color }} />;
      case 'star':
        return (
          <div className="shape star" style={{ color: color }}>
            ★
          </div>
        );
      default:
        return <div className="shape circle" style={{ backgroundColor: color }} />;
    }
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">💎 Match-3</h1>
        <div className="game-instructions">
          Swap adjacent pieces to create lines of 3 or more matching shapes. Longer matches give more points!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {gameOver ? `Game Over! Final Score: ${score}` : `Score: ${score} | Moves: ${moves}`}
        </div>

        {gameOver && (
          <div className="game-over-message">
            <h2>Game Over!</h2>
            <p>Final Score: {score}</p>
            <button onClick={resetGame} className="btn btn-primary">
              Play Again
            </button>
          </div>
        )}

        <div className="game-play-area">
          <div className="match3-board" key={animationKey}>
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const cellKey = `${rowIndex}-${colIndex}`;
                const isSelected = selectedCell && selectedCell[0] === rowIndex && selectedCell[1] === colIndex;
                const isMatching = matchingCells.has(cellKey);
                
                return (
                  <div
                    key={cellKey}
                    className={`match3-cell ${isSelected ? 'selected' : ''} ${
                      isAnimating ? 'animating' : ''
                    } ${isMatching ? 'matching' : ''}`}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    <div className="piece">
                      {renderShape(cell)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={resetGame}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Match3;